/**
 * Generic declarative reactive region for the HA framework.
 *
 * A scope is a sub-tree of the page whose content depends on a local state
 * and that knows how to re-fetch + re-render itself when the state changes.
 *
 * Conventions (attributs HTML) :
 *   - data-ha-scope="<id>"       → marque une région réactive (id arbitraire mais
 *                                   doit matcher un chemin extractible côté serveur,
 *                                   ex. "section.shell", "users.list", "stats")
 *   - data-ha-fetch="<query>"    → query string supplémentaire pour l'AJAX
 *                                   (ex. "?id=ha.st.dashboard.users")
 *   - data-ha-state='{"k":"v"}'  → state initial (JSON)
 *   - data-ha-render             → marqueur de l'élément dont le contenu sera remplacé
 *
 * Controls à l'intérieur d'un scope :
 *   - data-ha-set='{"filter":"role:admin"}'   → merge dans le state du scope ancêtre
 *   - data-ha-set='{"page":"+1"}'             → incrément numérique
 *   - data-ha-set='{"sort":"toggle:email"}'   → toggle dans un set
 *   - data-ha-bind="q" data-ha-bind-reset="page" → saisie liée au state ; les clés
 *     listées sont retirées à chaque saisie (nouvelle recherche → page 1). HA-274.
 *
 * Réponse serveur attendue (cf. trait TWithScopes PHP) :
 *   { t:"scope", id:"<id>", data:<jsonTree>, state:<currentState> }
 *
 * Le client remplace [data-ha-render] dans le scope par new Tag(data).
 */
import { Tag } from "../../HTML/Tag";
import { toMap, getDataRepository } from "../../../../HA";

interface ScopeState {
    [key: string]: any;
}

/**
 * One instance per scope element. Maintains local state, listens for
 * data-ha-set clicks bubbling up to its scope, fetches partial scope JSON
 * from /ha/app, swaps [data-ha-render] children.
 */
export class ScopeController {
    private readonly element: HTMLElement;
    private readonly scopeId: string;
    private readonly fetchUrl: string;
    private state: ScopeState;
    /** HA-274 : numéro de la dernière requête — une réponse plus ancienne arrivée après est ignorée. */
    private requestSeq = 0;

    constructor(element: HTMLElement) {
        this.element = element;
        this.scopeId = element.getAttribute("data-ha-scope") || "";
        this.fetchUrl = element.getAttribute("data-ha-fetch") || "/ha/app";
        this.state = this.readState();
        // Click delegation is handled at document level (cf. bindDocumentClickListener).
    }

    private readState(): ScopeState {
        const raw = this.element.getAttribute("data-ha-state");
        if (!raw) { return {}; }
        try { return JSON.parse(raw) || {}; }
        catch (e) { return {}; }
    }

    private writeState(): void {
        this.element.setAttribute("data-ha-state", JSON.stringify(this.state));
    }

    public applySetter(setter: HTMLElement): void {
        const raw = setter.getAttribute("data-ha-set");
        if (!raw) { return; }
        let payload: ScopeState;
        try { payload = JSON.parse(raw); }
        catch (e) { console.warn("[HA] data-ha-set JSON parse failed", raw, e); return; }
        this.applyState(payload);
    }

    /**
     * Merge a state diff and trigger a refresh. Used by both click setters and input bindings.
     * `resetKeys` (HA-274, `data-ha-bind-reset`) : clés RETIRÉES du state avant la fusion —
     * une nouvelle recherche repart page 1 au lieu de garder la page de la recherche précédente.
     */
    public applyState(payload: ScopeState, resetKeys: string[] = []): void {
        for (const key of resetKeys) {
            delete this.state[key];
        }
        for (const key of Object.keys(payload)) {
            this.state[key] = this.computeNewValue(this.state[key], payload[key]);
        }
        this.writeState();
        this.refresh();
    }

    /**
     * Resolve a setter expression against the current value :
     * - "+1" / "-1" : numeric increment when current is a number
     * - "toggle:<v>" : add/remove <v> from an array
     * - any other value : absolute assignment
     */
    private computeNewValue(current: any, expr: any): any {
        if (typeof expr !== "string") { return expr; }
        if (expr === "+1" || expr === "-1") {
            const n = parseInt(String(current || "0"), 10);
            return n + (expr === "+1" ? 1 : -1);
        }
        const m = /^([+-])(\d+)$/.exec(expr);
        if (m) {
            const n = parseInt(String(current || "0"), 10);
            return m[1] === "+" ? n + parseInt(m[2], 10) : n - parseInt(m[2], 10);
        }
        const toggle = /^toggle:(.+)$/.exec(expr);
        if (toggle) {
            const v = toggle[1];
            const arr: any[] = Array.isArray(current) ? current.slice() : [];
            const i = arr.indexOf(v);
            if (i >= 0) { arr.splice(i, 1); } else { arr.push(v); }
            return arr;
        }
        return expr;
    }

    /**
     * POST /ha/app with current state + _scope id, swap [data-ha-render] content.
     */
    private refresh(): void {
        const params = toMap(this.state);
        params.set("_scope", this.scopeId);

        const url = this.fetchUrl;
        const dataRepo = getDataRepository();
        if (!dataRepo) { return; }

        // Merge query string from data-ha-fetch into params (it usually carries `id`)
        const qIndex = url.indexOf("?");
        if (qIndex >= 0) {
            const query = url.substring(qIndex + 1);
            query.split("&").forEach((pair) => {
                const [k, v] = pair.split("=");
                if (k && !params.has(k)) {
                    params.set(decodeURIComponent(k), v ? decodeURIComponent(v) : "");
                }
            });
        }

        const me = this;
        const seq = ++this.requestSeq;
        dataRepo.fetchData(params).then((response: Map<any, any>) => {
            // Réponses hors d'ordre (frappe rapide, réseau lent) : seule la dernière requête
            // a le droit de réécrire le state et le rendu.
            if (seq !== me.requestSeq) { return; }
            const t = response.get("t");
            if (t !== "scope") { return; }
            // Le serveur peut avoir normalisé/refusé certaines valeurs (clamp page,
            // trim q…). On réaligne le state local sur ce qu'il a retenu.
            const serverState = response.get("state");
            if (serverState) {
                me.adoptServerState(serverState);
            }
            const data = response.get("data");
            if (!data) { return; }
            me.renderInto(data);
        }).catch((err: any) => {
            console.warn("[HA] scope fetch failed", this.scopeId, err);
        });
    }

    /** Replace local state with the server's canonical version. */
    private adoptServerState(serverState: any): void {
        const next: ScopeState = {};
        if (serverState instanceof Map) {
            serverState.forEach((v: any, k: any) => { next[String(k)] = v; });
        } else if (typeof serverState === "object") {
            for (const k of Object.keys(serverState)) { next[k] = serverState[k]; }
        } else {
            return;
        }
        this.state = next;
        this.writeState();
    }

    private renderInto(data: any): void {
        // data-ha-render peut être sur le scope lui-même OU sur un descendant.
        let target: HTMLElement | null = null;
        if (this.element.hasAttribute("data-ha-render")) {
            target = this.element;
        } else {
            target = this.element.querySelector("[data-ha-render]") as HTMLElement;
        }
        if (!target) { return; }

        const tag = new Tag(toMap(data));
        const el = tag.getElement();
        if (!el) { return; }

        if (target === this.element) {
            // Replace scope's CHILDREN, keep the scope element itself
            // (we need to preserve data-ha-scope/state/fetch attributes + the bound controller).
            // The new element's children become our children.
            while (target.firstChild) { target.removeChild(target.firstChild); }
            while (el.firstChild) { target.appendChild(el.firstChild); }
        } else {
            while (target.firstChild) { target.removeChild(target.firstChild); }
            target.appendChild(el);
        }
    }
}

/**
 * Scan the document for [data-ha-scope] elements and instantiate a
 * ScopeController for each. Idempotent : marks instantiated nodes so
 * a second scan ignores them.
 */
export function initScopes(root: ParentNode = document): void {
    const nodes = root.querySelectorAll("[data-ha-scope]:not([data-ha-scope-bound])");
    nodes.forEach((node: Element) => {
        ensureController(node as HTMLElement);
    });
}

/**
 * Document-level capture listener — un seul handler global qui :
 *   1. Capture le click AVANT que Anchor.ts ne l'arrête via stopImmediatePropagation
 *   2. Trouve le scope ancêtre du target
 *   3. Délègue à son ScopeController (le crée si pas encore bound)
 *
 * Avantage vs init-then-bind par scope : marche aussi pour les scopes ajoutés
 * dynamiquement au DOM (sans avoir besoin d'appeler initScopes après mutation).
 */
let documentListenerBound = false;
const scopeRegistry: Map<HTMLElement, ScopeController> = new Map();

function ensureController(scopeEl: HTMLElement): ScopeController {
    let controller = scopeRegistry.get(scopeEl);
    if (!controller) {
        controller = new ScopeController(scopeEl);
        scopeRegistry.set(scopeEl, controller);
        scopeEl.setAttribute("data-ha-scope-bound", "1");
    }
    return controller;
}

/** Debounce timer per (scopeEl + stateKey) to avoid refresh on every keystroke. */
const inputTimers: Map<string, number> = new Map();

export function bindDocumentClickListener(): void {
    if (documentListenerBound) { return; }
    documentListenerBound = true;

    // Click delegation : data-ha-set on <a>, <button>, anything.
    // Anchor.ts cède son onClick quand data-ha-set présent (soft-handoff),
    // donc bubble phase normale suffit. Cf. Anchor.ts:initialize.
    document.addEventListener("click", (e: Event) => {
        const target = e.target as HTMLElement;
        if (!target || !target.closest) { return; }
        const setter = target.closest("[data-ha-set]") as HTMLElement;
        if (!setter) { return; }
        const scopeEl = setter.closest("[data-ha-scope]") as HTMLElement;
        if (!scopeEl) { return; }
        e.preventDefault();
        ensureController(scopeEl).applySetter(setter);
    });

    // Input binding : data-ha-bind="<stateKey>" on <input> / <select> / <textarea>.
    // Updates scope.state[<stateKey>] then refreshes (debounced 300ms).
    document.addEventListener("input", (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (!target || !target.hasAttribute || !target.hasAttribute("data-ha-bind")) { return; }
        const scopeEl = target.closest("[data-ha-scope]") as HTMLElement;
        if (!scopeEl) { return; }
        const key = target.getAttribute("data-ha-bind") || "";
        if (!key) { return; }
        const value = target.value;
        const resetKeys = (target.getAttribute("data-ha-bind-reset") || "")
            .split(",").map((k) => k.trim()).filter((k) => k !== "");
        const controller = ensureController(scopeEl);
        const timerKey = (scopeEl.getAttribute("data-ha-scope") || "") + ":" + key;
        const existing = inputTimers.get(timerKey);
        if (existing) { window.clearTimeout(existing); }
        const timer = window.setTimeout(() => {
            controller.applyState({[key]: value}, resetKeys);
            inputTimers.delete(timerKey);
        }, 300);
        inputTimers.set(timerKey, timer);
    });
}
